import { bytesToBase64Url } from '../../lib/tools/encoding';
import { randomBytes } from '../../lib/tools/crypto';
import {
  encodePasteLinkPayload,
  encryptPasteText,
  PASTE_KEY_BYTES,
  PASTE_LINK_MAX_BYTES,
  PASTE_SALT_BYTES,
} from '../../lib/tools/paste';
import { readPastePrefill } from '../../lib/labs/terminal';

const form = document.querySelector<HTMLFormElement>('#paste-form');
const textInput = document.querySelector<HTMLTextAreaElement>('#paste-text');
const passphraseInput = document.querySelector<HTMLInputElement>('#paste-passphrase');
const statusEl = document.querySelector<HTMLParagraphElement>('#paste-status');
const errorEl = document.querySelector<HTMLParagraphElement>('#paste-error');
const sizeEl = document.querySelector<HTMLSpanElement>('#paste-size');
const shareRow = document.querySelector<HTMLDivElement>('#paste-share-row');
const shareLink = document.querySelector<HTMLInputElement>('#paste-share-link');
const copyShareButton = document.querySelector<HTMLButtonElement>('#paste-copy-share');
const clearButton = document.querySelector<HTMLButtonElement>('#paste-clear');
const resetButton = document.querySelector<HTMLButtonElement>('#paste-reset');
const cipherTextArea = document.querySelector<HTMLTextAreaElement>('#paste-ciphertext');
const copyCipherButton = document.querySelector<HTMLButtonElement>(
  '#paste-copy-ciphertext'
);

if (
  !form ||
  !textInput ||
  !passphraseInput ||
  !statusEl ||
  !errorEl ||
  !sizeEl ||
  !shareRow ||
  !shareLink ||
  !copyShareButton ||
  !clearButton ||
  !resetButton ||
  !cipherTextArea ||
  !copyCipherButton
) {
  throw new Error('Paste tool elements not found.');
}

function setStatus(message: string) {
  statusEl.textContent = message;
}

function setError(message: string) {
  errorEl.textContent = message;
  errorEl.classList.toggle('hidden', message.length === 0);
}

function updateSize() {
  const bytes = new TextEncoder().encode(textInput.value);
  sizeEl.textContent = `${bytes.length.toLocaleString()} bytes`;
  sizeEl.classList.toggle('text-rose-600', bytes.length > PASTE_LINK_MAX_BYTES);
}

function resetFields() {
  textInput.value = '';
  passphraseInput.value = '';
  shareRow.classList.add('hidden');
  shareLink.value = '';
  cipherTextArea.value = '';
  setError('');
  setStatus('');
  updateSize();
}

async function copyToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const temp = document.createElement('textarea');
  temp.value = value;
  temp.style.position = 'fixed';
  temp.style.opacity = '0';
  document.body.appendChild(temp);
  temp.select();
  document.execCommand('copy');
  temp.remove();
}

textInput.addEventListener('input', updateSize);
updateSize();

// Prefill the composer from the URL fragment, e.g. when opened from the hero
// terminal as `paste "your text"` (/lab/paste/#text=...). The fragment is used
// instead of a query string so the draft is never sent to the server, then it
// is cleared so a refresh or copied URL does not leak the text.
(function prefillFromHash() {
  const prefill = readPastePrefill(window.location.hash);
  if (!prefill) return;
  textInput.value = prefill;
  updateSize();
  setStatus('Loaded text from link. Review it, then encrypt.');
  history.replaceState(null, '', window.location.pathname + window.location.search);
})();

copyShareButton.addEventListener('click', async () => {
  if (!shareLink.value) return;
  await copyToClipboard(shareLink.value);
  setStatus('Share link copied to clipboard.');
});

copyCipherButton.addEventListener('click', async () => {
  if (!cipherTextArea.value) return;
  await copyToClipboard(cipherTextArea.value);
  setStatus('Ciphertext copied.');
});

clearButton.addEventListener('click', () => {
  textInput.value = '';
  updateSize();
  setStatus('Input cleared.');
});

resetButton.addEventListener('click', () => {
  resetFields();
});

form.addEventListener('submit', async (event) => {
  event.preventDefault();
  setError('');
  setStatus('');
  shareRow.classList.add('hidden');

  const text = textInput.value;
  if (!text.trim()) {
    setError('Enter a message to encrypt.');
    return;
  }

  const textBytes = new TextEncoder().encode(text);
  if (textBytes.length > PASTE_LINK_MAX_BYTES) {
    setError(
      `Paste is too large for a share link. Limit is ${PASTE_LINK_MAX_BYTES.toLocaleString()} bytes.`
    );
    return;
  }

  const passphrase = passphraseInput.value.trim() || undefined;

  try {
    setStatus('Encrypting locally...');

    const keyBytes = randomBytes(PASTE_KEY_BYTES);
    const salt = randomBytes(PASTE_SALT_BYTES);
    const iv = randomBytes(12);

    const cipher = await encryptPasteText({
      text,
      keyBytes,
      iv,
      salt,
      passphrase,
    });

    const payload = {
      version: 1 as const,
      key: bytesToBase64Url(keyBytes),
      cipher,
      createdAt: new Date().toISOString(),
    };
    const shareFragment = encodePasteLinkPayload(payload);
    const shareUrl = `${window.location.origin}/lab/paste/view#${shareFragment}`;
    shareLink.value = shareUrl;
    shareRow.classList.remove('hidden');
    cipherTextArea.value = cipher.ciphertext;

    const passphraseNote = passphrase ? ' Remember your passphrase to decrypt.' : '';
    setStatus(`Share link ready.${passphraseNote}`);
  } catch (error) {
    setError('Unable to encrypt the paste.');
    setStatus('');
  }
});
